import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FcGoogle } from "react-icons/fc";
import { IoClose } from "react-icons/io5";
import { useAuth } from "../contexts/AuthContext";
import PasswordStrengthIndicator from "./PasswordStrengthIndicator";

// Public-signup gate — read at build time from REACT_APP_PUBLIC_SIGNUP_ENABLED.
// When set to "false" in the deployed .env, the modal renders an "invitation
// only" banner instead of the form, matching the backend gate in
// auth/app/api/v1/endpoints/auth.py (which independently enforces 403).
// This is a UX layer; the backend is the actual security boundary.
const SIGNUPS_OPEN =
  String(process.env.REACT_APP_PUBLIC_SIGNUP_ENABLED ?? "true").toLowerCase() !==
  "false";

const SignupModal = ({ isOpen, onClose, onSwitchToLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [isRegistering, setIsRegistering] = useState(false);
  const navigate = useNavigate();
  const { register } = useAuth();

  const validate = () => {
    const errs = {};
    const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

    // Email validation
    if (!emailRegex.test(email)) errs.email = "Invalid email format";

    // Password validation
    if (password.length < 8) {
      errs.password = "Password must be at least 8 characters";
    } else {
      const hasUpperCase = /[A-Z]/.test(password);
      const hasLowerCase = /[a-z]/.test(password);
      const hasNumbers = /\d/.test(password);
      const hasSpecialChar = /[^A-Za-z0-9]/.test(password);

      if (!(hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar)) {
        errs.password =
          "Password must include uppercase, lowercase, number and special character";
      }
    }

    // Confirm password validation
    if (password !== confirmPassword)
      errs.confirmPassword = "Passwords do not match";

    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setIsRegistering(true);
    setMessage("Creating your account...");

    try {
      console.log("Attempting registration with:", { email });
      const response = await register(email, password);

      setMessage("Registration successful! Redirecting to profile...");

      // Clear form data
      setEmail("");
      setPassword("");
      setConfirmPassword("");

      // Close modal and navigate directly to profile completion
      // This ensures we navigate while the auth state is confirmed
      onClose();
      navigate("/profile-completion", { replace: true });
    } catch (err) {
      console.error("Registration error:", err);
      setMessage(
        err.message ||
          "Registration failed. Please check your connection and try again."
      );
      setIsRegistering(false);
    }
  };

  const handleGoogle = () => {
    window.location.href = `${process.env.REACT_APP_API_URL}/auth/google`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div
        className="p-8 bg-white/20 backdrop-blur-md rounded-lg border border-[var(--divider)] 
                 shadow-xl overflow-hidden text-white w-full max-w-md m-4 relative
                 animate-scaleIn"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/80 hover:text-white"
          disabled={isRegistering}
        >
          <IoClose size={24} />
        </button>

        <h2 className="ds-h2 mb-6 text-center">
          {SIGNUPS_OPEN ? "Create an Account" : "Invitation Only"}
        </h2>

        {!SIGNUPS_OPEN && (
          /*
           * Closed-signup banner. Replaces the form entirely when the gate is
           * off — both the email/password form AND the Google sign-up button
           * are hidden, since the backend rejects new-user creation on either
           * path.
           */
          <div className="space-y-4 text-center text-gray-100">
            <p>
              DeeTrade is currently onboarding early users by invitation while
              we stabilize the platform.
            </p>
            <p className="text-sm text-fg-3">
              If you'd like to be considered for early access, please reach out
              to the team. We'll get back to you with an invitation when a slot
              opens up.
            </p>
            <p className="mt-6 text-center text-sm text-gray-200">
              Already have an account?{" "}
              <button
                onClick={onSwitchToLogin}
                className="text-blue-400 hover:text-blue-500"
              >
                Log In
              </button>
            </p>
          </div>
        )}

        {SIGNUPS_OPEN && (
        <>
        <button
          onClick={handleGoogle}
          disabled={isRegistering}
          className="w-full flex items-center justify-center mb-4 px-4 py-2 border border-[var(--divider)] rounded 
                 hover:bg-white/10 transition-all text-white"
        >
          <FcGoogle className="w-5 h-5 mr-2" />
          Sign up with Google
        </button>

        <div className="flex items-center my-4">
          <div className="flex-1 h-px bg-gray-300/30"></div>
          <div className="px-3 text-sm text-gray-200">or</div>
          <div className="flex-1 h-px bg-gray-300/30"></div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block mb-1 text-gray-200">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isRegistering}
              className="w-full px-3 py-2 border border-[var(--divider)] rounded bg-white/10 text-white placeholder-gray-300"
              placeholder="your@email.com"
            />
            {errors.email && (
              <p className="text-red-400 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="block mb-1 text-gray-200">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isRegistering}
              className="w-full px-3 py-2 border border-[var(--divider)] rounded bg-white/10 text-white"
              placeholder="········"
            />
            <PasswordStrengthIndicator password={password} />
            {errors.password && (
              <p className="text-red-400 text-sm mt-1">{errors.password}</p>
            )}
          </div>

          <div>
            <label className="block mb-1 text-gray-200">Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isRegistering}
              className="w-full px-3 py-2 border border-[var(--divider)] rounded bg-white/10 text-white"
              placeholder="········"
            />
            {errors.confirmPassword && (
              <p className="text-red-400 text-sm mt-1">
                {errors.confirmPassword}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={isRegistering}
            className={`w-full text-white py-3 rounded-lg transition-all transform ${
              isRegistering
                ? "bg-blue-500 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 hover:scale-[1.02]"
            }`}
          >
            {isRegistering ? "Creating Account..." : "Create Account"}
          </button>
        </form>

        {message && (
          <p
            className={`mt-4 text-center ${
              message.includes("successful") || message.includes("Creating")
                ? "text-green-400"
                : "text-red-400"
            }`}
          >
            {message}
          </p>
        )}

        <p className="mt-4 text-center text-sm text-gray-200">
          Already have an account?{" "}
          <button
            onClick={onSwitchToLogin}
            className="text-blue-400 hover:text-blue-500"
            disabled={isRegistering}
          >
            Log In
          </button>
        </p>
        </>
        )}
      </div>
    </div>
  );
};

export default SignupModal;
